import { useCallback, useContext, useState } from 'react'

import { ToastContext, useFetch } from '@reveni-io/reveni-ui'

import AppContext from '@/context/AppContext'
import services from '@/services/services'

import { bankAccountErrorTypes } from './constants'
import { getCreateBankAccountPayload } from './helpers'

const useBankAccount = () => {
  const { context, setContext } = useContext(AppContext)
  const [bankAccount, setBankAccount] = useState(context?.orderInfo?.bankAccount || null)
  const [bankAccounts, setBankAccounts] = useState(context?.bankAccounts || null)
  const [newBankAccount, setNewBankAccount] = useState(context?.orderInfo?.customer_iban || null)
  const [newBankAccountError, setNewBankAccountError] = useState(false)
  const [errorType, setErrorType] = useState(null)

  const { setToastInfo } = useContext(ToastContext)
  const { fetchData: createBankAccountService, loading: creatingBankAccount } = useFetch(services.createBankAccount, {
    doInitialRequest: false,
  })

  const { fetchData: fetchBankAccounts, loading: fetchingBankAccounts } = useFetch(services.getBankAccounts, {
    doInitialRequest: false,
  })

  const onChangeBankAccount = bankAccount => {
    setBankAccount(bankAccount)
    setContext(context => ({ ...context, orderInfo: { ...context.orderInfo, bankAccount } }))
  }

  const onChangeNewBankAccount = ({ value, error }) => {
    setNewBankAccount(value)
    setNewBankAccountError(error)
  }

  const createBankAccount = async ({ newBankAccount, firstName, lastName, callback, verificationCodeId }) => {
    const payload = getCreateBankAccountPayload(newBankAccount, firstName, lastName, verificationCodeId)

    try {
      const data = await createBankAccountService(payload)
      onChangeBankAccount(data)
      if (callback) callback()
    } catch (err) {
      const data = err?.response?.data

      if (data?.bank_account_number) {
        setErrorType(bankAccountErrorTypes.IBAN)
      } else if (data?.non_field_errors) {
        setErrorType(bankAccountErrorTypes.UK)
      } else {
        setToastInfo({ show: true, message: err.errorMessage, type: 'error' })
      }
    }
  }

  const getBankAccounts = useCallback(async () => {
    try {
      const data = await fetchBankAccounts({ type: context?.orderAmount?.currency })
      setBankAccounts(data?.results)
      setContext(context => ({ ...context, bankAccounts: data?.results }))

      return data?.results
    } catch (err) {
      setToastInfo({ show: true, message: err.errorMessage, type: 'error' })
    }
  }, [context?.orderAmount?.currency, fetchBankAccounts, setContext, setToastInfo])

  return {
    bankAccount,
    bankAccounts,
    onChangeBankAccount,
    createBankAccount,
    creatingBankAccount,
    errorType,
    newBankAccountError,
    newBankAccount,
    onChangeNewBankAccount,
    getBankAccounts,
    fetchingBankAccounts,
  }
}

export default useBankAccount
